// Création / modification d'un bon de commande (brouillon).
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Grid, IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import { achatsAPI, intrantAPI } from '../../services/api';
import { apiError, fcfa, today } from '../../utils/format';
import { tokens } from '../../theme';
import { useFer, kgParBarre, enBarres } from '../../utils/fer';

const emptyLine = () => ({ key: Math.random(), intrant_id: '', designation: '', unite: '', quantite: '', prix_unitaire: '' });
const num = (v) => Number(String(v ?? '').replace(/\s/g, '').replace(',', '.')) || 0;

const BonCommandeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fournisseurs, setFournisseurs] = useState([]);
  const [intrants, setIntrants] = useState([]);
  const [f, setF] = useState({ fournisseur_id: '', date_commande: today(), date_livraison_prevue: '', lieu_livraison: '', conditions: '', commentaire: '', taux_tva: 18 });
  const [lignes, setLignes] = useState([emptyLine()]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const ferT = useFer();
  const intrantDe = (l) => intrants.find((x) => x.id === l.intrant_id);
  /** Ligne liée à un fer à béton : le choix d'unité kg ou T est autorisé. */
  const estFer = (l) => { const it = intrantDe(l); return !!it && /^FER/i.test(it.categorie || ''); };
  /** kg par barre si la ligne est un fer saisi en kg (pas en tonnes). */
  const kpbLigne = (l) => { const it = intrantDe(l); return it && l.unite === 'kg' && /^FER/i.test(it.categorie || '') ? kgParBarre(it.code, null, ferT) : null; };
  // Bascule kg <-> T sur une ligne de fer en conservant le montant (quantité et prix unitaire convertis).
  const changerUnite = (i, u) => setLignes((prev) => prev.map((l, j) => {
    if (j !== i || l.unite === u) return l;
    const q = num(l.quantite); const pu = num(l.prix_unitaire);
    const fq = l.unite === 'kg' && u === 'T' ? 0.001 : l.unite === 'T' && u === 'kg' ? 1000 : 1;
    return {
      ...l, unite: u, barres: '',
      quantite: q ? String(Math.round(q * fq * 1000) / 1000).replace('.', ',') : l.quantite,
      prix_unitaire: pu ? String(Math.round(pu / fq)) : l.prix_unitaire,
    };
  }));

  useEffect(() => {
    achatsAPI.fournisseurs().then((r) => setFournisseurs(r.data)).catch(() => {});
    intrantAPI.list().then((r) => setIntrants(r.data)).catch(() => {});
    if (id) {
      achatsAPI.commande(id).then(({ data }) => {
        if (data.statut !== 'BROUILLON') { navigate(`/achats/commandes/${id}`, { replace: true }); return; }
        setF({
          fournisseur_id: data.fournisseur_id, date_commande: data.date_commande, date_livraison_prevue: data.date_livraison_prevue || '',
          lieu_livraison: data.lieu_livraison || '', conditions: data.conditions || '', commentaire: data.commentaire || '', taux_tva: data.taux_tva,
        });
        setLignes(data.lignes.map((l) => ({ ...l, key: l.id, intrant_id: l.intrant_id || '' })));
      }).catch((e) => setError(apiError(e)));
    }
  }, [id, navigate]);

  const setLine = (i, changes) => setLignes((prev) => prev.map((l, j) => (j === i ? { ...l, ...changes } : l)));
  const pickIntrant = (i, value) => {
    const it = intrants.find((x) => x.id === value);
    setLine(i, it ? { intrant_id: it.id, designation: it.designation, unite: it.unite } : { intrant_id: '', unite: '' });
  };
  const ht = lignes.reduce((s, l) => s + Math.round(num(l.quantite) * num(l.prix_unitaire)), 0);
  const tva = Math.round((ht * num(f.taux_tva)) / 100);

  const save = async () => {
    setSaving(true); setError('');
    try {
      const data = { ...f, lignes: lignes.filter((l) => l.designation || l.intrant_id || l.quantite) };
      const r = id ? await achatsAPI.updateCommande(id, data) : await achatsAPI.createCommande(data);
      navigate(`/achats/commandes/${id || r.data.id}`);
    } catch (e) { setError(apiError(e)); window.scrollTo(0, 0); } finally { setSaving(false); }
  };

  return (
    <Layout title={id ? 'Modifier le bon de commande' : 'Nouveau bon de commande'} plain>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Fournisseur" required value={f.fournisseur_id} onChange={(e) => setF({ ...f, fournisseur_id: e.target.value })}
                helperText={fournisseurs.length ? ' ' : "Aucun fournisseur : créez-le d'abord dans « Fournisseurs »"}>
                {fournisseurs.map((x) => <MenuItem key={x.id} value={x.id}>{x.nom}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField type="date" fullWidth label="Date de commande" InputLabelProps={{ shrink: true }} value={f.date_commande} onChange={(e) => setF({ ...f, date_commande: e.target.value })} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField type="date" fullWidth label="Livraison souhaitée" InputLabelProps={{ shrink: true }} value={f.date_livraison_prevue} onChange={(e) => setF({ ...f, date_livraison_prevue: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Lieu de livraison" value={f.lieu_livraison} placeholder="Usine DOCC" onChange={(e) => setF({ ...f, lieu_livraison: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Conditions (paiement, transport…)" value={f.conditions} onChange={(e) => setF({ ...f, conditions: e.target.value })} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>Article en stock</TableCell><TableCell>Désignation</TableCell><TableCell sx={{ width: 120 }}>Unité</TableCell>
              <TableCell sx={{ width: 120 }}>Quantité</TableCell><TableCell sx={{ width: 140 }}>Prix unitaire HT</TableCell><TableCell align="right" sx={{ width: 140 }}>Montant HT</TableCell><TableCell sx={{ width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lignes.map((l, i) => (
              <TableRow key={l.key}>
                <TableCell>
                  <TextField select size="small" fullWidth value={l.intrant_id} onChange={(e) => pickIntrant(i, e.target.value)} SelectProps={{ displayEmpty: true, SelectDisplayProps: { 'aria-label': `Article ligne ${i + 1}`, 'aria-labelledby': undefined } }}>
                    <MenuItem value=""><em>Autre (hors stock)</em></MenuItem>
                    {intrants.map((x) => <MenuItem key={x.id} value={x.id}>{x.designation}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell><TextField size="small" fullWidth value={l.designation} inputProps={{ 'aria-label': `Désignation ligne ${i + 1}` }} onChange={(e) => setLine(i, { designation: e.target.value })} /></TableCell>
                <TableCell>
                  {estFer(l) ? (
                    <TextField select size="small" fullWidth value={l.unite || 'kg'} SelectProps={{ 'aria-label': `Unité ligne ${i + 1}` }}
                      onChange={(e) => changerUnite(i, e.target.value)}>
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="T">T (tonne)</MenuItem>
                    </TextField>
                  ) : (
                    <TextField size="small" fullWidth value={l.unite} disabled={!!l.intrant_id} inputProps={{ 'aria-label': `Unité ligne ${i + 1}` }} onChange={(e) => setLine(i, { unite: e.target.value })} />
                  )}
                </TableCell>
                <TableCell>
                  <TextField size="small" fullWidth value={l.quantite} inputProps={{ 'aria-label': `Quantité ligne ${i + 1}`, inputMode: 'decimal' }} onChange={(e) => setLine(i, { quantite: e.target.value, barres: '' })} />
                  {kpbLigne(l) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                      <TextField size="small" variant="standard" placeholder="barres" value={l.barres || ''} sx={{ width: 70 }}
                        inputProps={{ 'aria-label': `Barres ligne ${i + 1}`, inputMode: 'numeric' }}
                        onChange={(e) => { const b = e.target.value.replace(/[^\d]/g, ''); setLine(i, { barres: b, quantite: b ? String(Math.round(Number(b) * kpbLigne(l) * 10) / 10).replace('.', ',') : '' }); }} />
                      <Typography sx={{ fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {l.barres ? `× ${String(kpbLigne(l)).replace('.', ',')} kg` : num(l.quantite) ? `≈ ${String(enBarres(num(l.quantite), kpbLigne(l))).replace('.', ',')} barres` : `1 barre = ${String(kpbLigne(l)).replace('.', ',')} kg`}
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell><TextField size="small" fullWidth value={l.prix_unitaire} inputProps={{ 'aria-label': `Prix ligne ${i + 1}`, inputMode: 'decimal' }} onChange={(e) => setLine(i, { prix_unitaire: e.target.value })} /></TableCell>
                <TableCell align="right" sx={{ fontFamily: tokens.mono }}>{fcfa(num(l.quantite) * num(l.prix_unitaire))}</TableCell>
                <TableCell padding="checkbox">
                  <IconButton size="small" aria-label={`Supprimer la ligne ${i + 1}`} disabled={lignes.length === 1} onClick={() => setLignes(lignes.filter((_, j) => j !== i))}><DeleteOutline fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 2, gap: 2, flexWrap: 'wrap' }}>
          <Button startIcon={<AddOutlined />} onClick={() => setLignes([...lignes, emptyLine()])}>Ajouter une ligne</Button>
          <Box sx={{ minWidth: 300 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Total HT</span><b style={{ fontFamily: tokens.mono }}>{fcfa(ht)} F</b></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 0.5 }}>
              <span>TVA <TextField size="small" value={f.taux_tva} onChange={(e) => setF({ ...f, taux_tva: e.target.value })} inputProps={{ 'aria-label': 'Taux de TVA', style: { width: 36, padding: '2px 6px' } }} /> %</span>
              <span style={{ fontFamily: tokens.mono }}>{fcfa(tva)} F</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: 1, borderColor: 'divider', pt: 0.5 }}>
              <Typography sx={{ fontWeight: 700 }}>Total TTC</Typography><Typography sx={{ fontWeight: 700, fontFamily: tokens.mono }}>{fcfa(ht + tva)} F</Typography>
            </Box>
          </Box>
        </Box>
      </Card>
      <TextField fullWidth multiline minRows={2} label="Commentaire interne" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" disabled={saving} onClick={save}>Enregistrer le brouillon</Button>
        <Button onClick={() => navigate(id ? `/achats/commandes/${id}` : '/achats')}>Annuler</Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Les articles « en stock » (fers, ciment, granulats…) entreront automatiquement dans le stock des intrants à la réception.
        Le fer peut être commandé en kg ou en tonnes (T) ; le stock reste tenu en kg (1 T = 1000 kg).
      </Typography>
    </Layout>
  );
};

export default BonCommandeForm;
